/*
 * <variant-shade> — drives variant-color dependent content on the product page.
 *
 * It listens for the theme's `variant:change` event (dispatched by <variant-picker>)
 * and shows the child whose `data-shade` value matches the selected variant. The shade
 * is matched against the variant's option values (and falls back to the variant title),
 * so the merchant-entered key must equal the shade option value (e.g. NUDE, MUSE...).
 *
 * Modes:
 *  - "toggle"    (default): only the matching [data-shade] child is visible (others hidden)
 *  - "highlight": all [data-shade] children stay visible, the matching one gets `.is-active`
 *
 * Optional local controls: elements with `data-shade-control="<SHADE>"` switch the displayed
 * shade on click (used by the shade-preview swatch row), independently of the variant picker.
 */
(function () {
  if (window.customElements.get('variant-shade')) {
    return;
  }

  function getCurrentVariant() {
    var node =
      document.querySelector('variant-picker[context="main_product"] script[data-variant]') ||
      document.querySelector('variant-picker script[data-variant]');

    if (!node) {
      return null;
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  var VariantShade = class extends HTMLElement {
    connectedCallback() {
      this.mode = this.getAttribute('mode') || 'toggle';
      this._items = Array.from(this.querySelectorAll('[data-shade]'));
      this._shades = this._items
        .map(function (el) {
          return el.getAttribute('data-shade');
        })
        .filter(function (value, index, all) {
          return value && all.indexOf(value) === index;
        });

      this._onVariantChange = function (event) {
        // The visitor has engaged with the selection — let the active shade pop forward.
        this.classList.add('is-engaged');
        this.apply(event.detail && event.detail.variant);
      }.bind(this);

      document.addEventListener('variant:change', this._onVariantChange);

      // Swatch / preview controls. A control that also carries a variant id selects
      // the real variant (so the top gallery, price and picker follow); otherwise it
      // just previews the shade locally.
      this.querySelectorAll('[data-shade-control]').forEach(
        function (control) {
          control.addEventListener(
            'click',
            function () {
              this.classList.add('is-engaged');

              if (control.hasAttribute('data-shade-select')) {
                this.selectVariant(control);
              }

              this.show(control.getAttribute('data-shade-control'));
            }.bind(this)
          );
        }.bind(this)
      );

      // Clickable shade rows (shade list) select their variant in the variant-picker,
      // which — through the theme's variant:change flow — scrolls the gallery and
      // updates the top variant swatch / option label.
      this.querySelectorAll('[data-shade-select]:not([data-shade-control])').forEach(
        function (control) {
          control.addEventListener(
            'click',
            function () {
              this.selectVariant(control);
            }.bind(this)
          );
          control.addEventListener(
            'keydown',
            function (event) {
              if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault();
                this.selectVariant(control);
              }
            }.bind(this)
          );
        }.bind(this)
      );

      this.apply(getCurrentVariant());

      // Show the current variant's border from the start (apply() above already ran
      // without engaging, so the initial selection doesn't trigger an auto-scroll).
      this.classList.add('is-engaged');
    }

    selectVariant(control) {
      var optionValueId = control.getAttribute('data-shade-select');

      if (!optionValueId) {
        return false;
      }

      var formId = control.getAttribute('data-form-id');
      var input;

      if (formId) {
        input = document.querySelector(
          'input[data-option-position][form="' + formId + '"][value="' + optionValueId + '"]'
        );
      } else {
        // No form id (e.g. the shade-preview swatches live in a separate section) —
        // target the main product's variant picker.
        input =
          document.querySelector(
            'variant-picker[context="main_product"] input[data-option-position][value="' + optionValueId + '"]'
          ) || document.querySelector('input[data-option-position][value="' + optionValueId + '"]');
      }

      if (!input || input.checked) {
        return false;
      }

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    matchShade(variant) {
      if (!variant) {
        return null;
      }

      var options = (variant.options || []).map(function (option) {
        return String(option || '').toLowerCase().trim();
      });
      var title = String(variant.title || '').toLowerCase();

      for (var i = 0; i < this._shades.length; i++) {
        var shade = String(this._shades[i]).toLowerCase().trim();

        if (options.indexOf(shade) !== -1) {
          return this._shades[i];
        }
      }

      // Fallback: the shade name appears in the variant title (e.g. "NUDE / 0.6ml")
      for (var j = 0; j < this._shades.length; j++) {
        var fallback = String(this._shades[j]).toLowerCase().trim();

        if (fallback && title.indexOf(fallback) !== -1) {
          return this._shades[j];
        }
      }

      return null;
    }

    apply(variant) {
      this.show(this.matchShade(variant) || this._shades[0]);
    }

    show(shade) {
      if (!shade) {
        return;
      }

      // Did the visitor actually pick a different shade? Selecting via a swatch
      // makes show() run twice (once from the click, once from the resulting
      // variant:change echo). We only auto-scroll on a real change, so the echo
      // never yanks the strip back while the visitor is scrolling it by hand.
      var changed = shade !== this._current;
      this._current = shade;

      var activeItem = null;

      this._items.forEach(
        function (item) {
          var match = item.getAttribute('data-shade') === shade;

          if (this.mode === 'highlight') {
            item.classList.toggle('is-active', match);
          } else {
            item.hidden = !match;
          }

          if (match) {
            activeItem = item;
          }
        }.bind(this)
      );

      this.querySelectorAll('[data-shade-control]').forEach(function (control) {
        control.classList.toggle('is-active', control.getAttribute('data-shade-control') === shade);
      });

      // Once engaged, scroll to the freshly selected shade — in whichever
      // direction it sits (e.g. picking the leftmost while scrolled fully right
      // slides back left). Only on a real change, so re-selecting the current
      // shade or the echoed variant:change never fights a manual scroll.
      if (changed && activeItem && this.classList.contains('is-engaged')) {
        this.scrollIntoView(activeItem);
      }
    }

    scrollIntoView(item) {
      var scroller = item.closest('.shade-preview__row');

      if (!scroller) {
        return;
      }

      var itemRect = item.getBoundingClientRect();
      var viewRect = scroller.getBoundingClientRect();
      // Match the CSS scroll-padding-inline (1.5rem) so the landing position
      // lines up with the scroll-snap point.
      var pad = 24;
      var current = scroller.scrollLeft;
      var maxScroll = scroller.scrollWidth - scroller.clientWidth;

      // Resolve the item's edges to absolute positions within the scroll content,
      // independent of the current (possibly mid-animation) scroll offset. This
      // way show() being called twice — once from the click and once from the
      // resulting variant:change — both target the SAME destination instead of
      // chaining two relative scrollBy calls that fight and land short.
      var itemLeft = current + (itemRect.left - viewRect.left);
      var itemRight = current + (itemRect.right - viewRect.right);
      var target;

      if (itemLeft - pad < current) {
        target = itemLeft - pad; // off to the left — bring its left edge in
      } else if (itemRight + pad > current) {
        target = itemRight + pad; // off to the right — bring its right edge in
      } else {
        return; // already fully visible
      }

      target = Math.max(0, Math.min(target, maxScroll));

      if (Math.abs(target - current) > 1) {
        scroller.scrollTo({ left: target, behavior: 'smooth' });
      }
    }
  };

  window.customElements.define('variant-shade', VariantShade);
})();
